import { useState, useEffect } from 'react'
import { supabase } from '../services/supabase'
import { User } from '@supabase/supabase-js'
import {
  setUsuarioEmpresaId,
  setUsuarioLogadoId,
  clearAllCache,
  setSoftwareHouse,
  setSoftwareHouseEmpresaId,
  getEfetiveEmpresaId
} from './authCache'

// Re-exportar funções de cache para compatibilidade
export {
  getUsuarioEmpresaId,
  getUsuarioLogadoId,
  clearUsuarioCache,
  isSoftwareHouse,
  getSoftwareHouseEmpresaId,
  setSoftwareHouseEmpresaId,
  getEfetiveEmpresaId
} from './authCache'

// Interface para dados do usuário da tabela usuarios
interface UsuarioLogado {
  id: string
  empresa_id: string
  nome: string
  email: string
  perfil: string
  ativo: boolean
  software_house: boolean
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [usuarioLogado, setUsuarioLogado] = useState<UsuarioLogado | null>(null)
  const [loading, setLoading] = useState(true)
  // A busca em `usuarios` é assíncrona e NÃO bloqueia o `loading`. Este sinal
  // diz se ela já terminou — sem ele, quem consome confunde "ainda buscando"
  // com "não existe" e derruba o usuário para o login no meio do caminho.
  const [usuarioCarregado, setUsuarioCarregado] = useState(false)
  const [empresaAtual, setEmpresaAtual] = useState<string | null>(null)

  // Buscar dados do usuário na tabela usuarios
  const fetchUsuarioLogado = async (authUserId: string): Promise<UsuarioLogado | null> => {
    try {
      const { data, error } = await supabase
        .from('usuarios')
        .select('id, empresa_id, nome, email, perfil, ativo, software_house')
        .eq('auth_user_id', authUserId)
        .eq('ativo', true)
        .single()

      if (error) {
        console.error('❌ Erro ao buscar usuário:', error)
        return null
      }

      // Atualizar cache global
      setUsuarioEmpresaId(data.empresa_id)
      setUsuarioLogadoId(data.id)

      // Configurar modo software_house se aplicável
      setSoftwareHouse(data.software_house || false)

      return data
    } catch (err) {
      console.error('❌ Erro ao buscar usuário logado:', err)
      return null
    }
  }

  useEffect(() => {
    let mounted = true
    // Auth user cujos dados de `usuarios` já foram carregados. O supabase-js dispara
    // SIGNED_IN toda vez que a aba volta a ficar visível; recarregar nesse caso
    // derrubava o ProtectedRoute para o Loading e remontava a página inteira.
    let usuarioCarregadoPara: string | null = null

    // Safety handling to prevent infinite loading
    const safetyTimeout = setTimeout(() => {
      if (mounted && loading) {
        console.warn('⚠️ Authentication check timed out, forcing load completion')
        setLoading(false)
      }
    }, 5000)

    // Verificar sessão atual
    const getSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession()

        if (error) {
          console.error('❌ Erro ao obter sessão:', error)
          // Don't throw, just proceed as unauthenticated
        }

        const currentUser = session?.user || null
        if (mounted) setUser(currentUser)

        // Se tem usuário autenticado, buscar dados da tabela usuarios
        if (currentUser) {
          // Non-blocking fetch for user details
          fetchUsuarioLogado(currentUser.id).then(usuario => {
            if (usuario) usuarioCarregadoPara = currentUser.id
            if (mounted) {
              setUsuarioLogado(usuario)
              setUsuarioCarregado(true)
            }
          })
        } else if (mounted) {
          setUsuarioCarregado(true)
        }
      } catch (error) {
        console.error('❌ Erro ao verificar sessão:', error)
      } finally {
        if (mounted) {
          clearTimeout(safetyTimeout)
          setLoading(false)
        }
      }
    }

    getSession()

    // Escutar mudanças de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        try {
          const currentUser = session?.user || null
          const mesmoUsuarioJaCarregado = !!currentUser && currentUser.id === usuarioCarregadoPara

          // Mesmo usuário em SIGNED_IN/TOKEN_REFRESHED: manter a referência para não disparar efeitos dependentes de `user`
          if (mounted) {
            if (mesmoUsuarioJaCarregado && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
              setUser(prev => (prev?.id === currentUser.id ? prev : currentUser))
            } else {
              setUser(currentUser)
            }
          }

          if (currentUser && event === 'SIGNED_IN' && !mesmoUsuarioJaCarregado) {
            // Non-blocking fetch on sign in too
            if (mounted) setUsuarioCarregado(false)
            fetchUsuarioLogado(currentUser.id).then(usuario => {
              if (usuario) usuarioCarregadoPara = currentUser.id
              if (mounted) {
                setUsuarioLogado(usuario)
                setUsuarioCarregado(true)
              }
            })
          }

          // Redirecionar baseado no estado de autenticação
          if (event === 'SIGNED_OUT') {
            usuarioCarregadoPara = null
            if (mounted) {
              clearAllCache()
              setUsuarioLogado(null)
              setUsuarioCarregado(true)
            }
            window.location.href = '/login'
          }
        } catch (error) {
          console.error('❌ Erro no onAuthStateChange:', error)
        } finally {
          if (mounted) {
            clearTimeout(safetyTimeout)
            setLoading(false)
          }
        }
      }
    )

    return () => {
      mounted = false
      clearTimeout(safetyTimeout)
      subscription.unsubscribe()
    }
  }, [])

  const logout = async () => {
    try {
      setLoading(true)
      const { error } = await supabase.auth.signOut()
      if (error) throw error

      // Redirecionar para página de login
      window.location.href = '/login'
    } catch (error: any) {
      console.error('Erro ao fazer logout:', error.message)
    } finally {
      setLoading(false)
    }
  }

  const getUserData = () => {
    if (!user) return null

    // Priorizar dados do usuário da tabela usuarios
    if (usuarioLogado) {
      return {
        id: usuarioLogado.id,
        authId: user.id,
        email: usuarioLogado.email,
        nome: usuarioLogado.nome,
        perfil: usuarioLogado.perfil,
        empresaId: usuarioLogado.empresa_id,
        avatar: user.user_metadata?.avatar_url || null
      }
    }

    // Fallback para dados do auth
    return {
      id: user.id,
      authId: user.id,
      email: user.email || '',
      nome: user.user_metadata?.nome || user.email?.split('@')[0] || 'Usuário',
      perfil: null,
      empresaId: null,
      avatar: user.user_metadata?.avatar_url || null
    }
  }

  // Função para trocar empresa (software_house)
  const selecionarEmpresa = (empresaId: string) => {
    if (usuarioLogado?.software_house) {
      setSoftwareHouseEmpresaId(empresaId)
      setEmpresaAtual(empresaId)
    }
  }

  // Atualizar empresa atual quando houver mudança
  useEffect(() => {
    const efetiva = getEfetiveEmpresaId() || usuarioLogado?.empresa_id || null
    if (efetiva !== empresaAtual) {
      setEmpresaAtual(efetiva)
    }
  }, [usuarioLogado, empresaAtual])

  return {
    user,
    usuarioLogado,
    usuarioCarregado,
    userData: getUserData(),
    empresaId: empresaAtual,
    usuarioId: usuarioLogado?.id || null,
    loading,
    logout,
    isAuthenticated: !!user,
    isSoftwareHouse: usuarioLogado?.software_house || false,
    selecionarEmpresa
  }
}